import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Star, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function Evaluations() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ avg_quality: 0, avg_hallucination: 0, count: 0 });
  const [agents, setAgents] = useState([]);
  const [dlg, setDlg] = useState(false);
  const [form, setForm] = useState({ agent_id: "ceo", quality_score: 4, hallucination_score: 1 });

  const load = () => {
    api.get("/evaluations").then((r) => { setItems(r.data.evaluations); setSummary(r.data.summary); });
    api.get("/agents").then((r) => setAgents(r.data.agents.filter((a) => a.status === "active")));
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/evaluations", {
        agent_id: form.agent_id,
        quality_score: parseInt(form.quality_score),
        hallucination_score: parseInt(form.hallucination_score),
        notes: form.notes,
      });
      toast.success("Evaluación registrada");
      setForm({ agent_id: "ceo", quality_score: 4, hallucination_score: 1 });
      setDlg(false);
      load();
    } catch { toast.error("Fallo"); }
  };

  return (
    <div className="p-8 space-y-6" data-testid="evaluations-page">
      <div className="border-b border-[var(--border)] pb-5 flex items-end justify-between">
        <div>
          <div className="label-mono mb-2">CALIDAD DE AGENTES · EVALS</div>
          <h1 className="font-display font-black text-4xl tracking-tight">Evaluaciones</h1>
          <p className="text-sm text-[var(--text-dim)] mt-1">
            Puntuación de respuestas: calidad 1-5 y alucinación 0-5. Sirve para NIST AI RMF + ISO/IEC 42001.
          </p>
        </div>
        <Dialog open={dlg} onOpenChange={setDlg}>
          <DialogTrigger asChild>
            <Button className="rounded-none bg-[var(--accent)] text-[var(--bg)] hover:bg-[var(--accent)]/80" data-testid="ev-add">
              <Plus className="w-4 h-4 mr-2" /> Nueva evaluación
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-none bg-[var(--surface)] border-[var(--border)]">
            <DialogHeader><DialogTitle className="font-display">Registrar eval</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="label-mono">Agente</Label>
                <Select value={form.agent_id} onValueChange={(v) => setForm({ ...form, agent_id: v })}>
                  <SelectTrigger className="rounded-none bg-[var(--bg)] border-[var(--border)] font-mono" data-testid="ev-agent"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none bg-[var(--surface)] border-[var(--border)] max-h-64">
                    {agents.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="label-mono">Calidad (1-5)</Label>
                  <Input type="number" min="1" max="5" value={form.quality_score}
                         onChange={(e) => setForm({ ...form, quality_score: e.target.value })}
                         className="rounded-none bg-[var(--bg)] border-[var(--border)] font-mono" data-testid="ev-q" />
                </div>
                <div>
                  <Label className="label-mono">Alucinación (0-5)</Label>
                  <Input type="number" min="0" max="5" value={form.hallucination_score}
                         onChange={(e) => setForm({ ...form, hallucination_score: e.target.value })}
                         className="rounded-none bg-[var(--bg)] border-[var(--border)] font-mono" data-testid="ev-h" />
                </div>
              </div>
              <div>
                <Label className="label-mono">Notas</Label>
                <Textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                          className="rounded-none bg-[var(--bg)] border-[var(--border)] font-mono min-h-[80px]" data-testid="ev-notes" />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={create} className="rounded-none bg-[var(--accent)] text-[var(--bg)] hover:bg-[var(--accent)]/80" data-testid="ev-submit">Registrar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <Stat label="Total evaluaciones" value={summary.count} icon={Star} />
        <Stat label="Calidad media" value={`${summary.avg_quality} / 5`} icon={Star} tone="good" />
        <Stat label="Alucinación media" value={`${summary.avg_hallucination} / 5`} icon={AlertTriangle}
              tone={summary.avg_hallucination > 2 ? "warn" : "default"} />
      </div>

      <div className="border border-[var(--border)] bg-[var(--surface)]">
        <div className="grid grid-cols-[100px_100px_100px_1fr_120px] label-mono py-3 px-4 border-b border-[var(--border)] bg-[var(--surface-2)]">
          <div>AGENTE</div>
          <div>CALIDAD</div>
          <div>HALLUC</div>
          <div>NOTAS</div>
          <div className="text-right">CUÁNDO</div>
        </div>
        {items.length === 0 ? (
          <div className="p-8 text-center text-sm text-[var(--text-dim)] font-mono">Aún sin evaluaciones.</div>
        ) : items.map((e) => (
          <div key={e.id} className="grid grid-cols-[100px_100px_100px_1fr_120px] py-3 px-4 border-b border-[var(--border)] font-mono text-xs items-center" data-testid={`ev-${e.id}`}>
            <div className="uppercase text-[var(--accent)]">{e.agent_id}</div>
            <div>
              <span className="text-[var(--good)]">{"★".repeat(e.quality_score)}</span>
              <span className="text-[var(--text-dim)]">{"☆".repeat(5 - e.quality_score)}</span>
            </div>
            <div>
              <span className={e.hallucination_score > 2 ? "text-[var(--danger)]" : "text-[var(--warn)]"}>
                {"●".repeat(e.hallucination_score)}
              </span>
              <span className="text-[var(--text-dim)]">{"○".repeat(5 - e.hallucination_score)}</span>
            </div>
            <div className="text-[var(--text-dim)] truncate">{e.notes || "—"}</div>
            <div className="text-right text-[var(--text-dim)]">
              {new Date(e.created_at).toLocaleDateString("es-ES")}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

const Stat = ({ label, value, icon: Icon, tone = "default" }) => (
  <div className="border border-[var(--border)] bg-[var(--surface)] p-5">
    <div className="flex items-start justify-between mb-3">
      <div className="label-mono">{label}</div>
      <Icon className={`w-4 h-4 ${tone === "warn" ? "text-[var(--warn)]" : tone === "good" ? "text-[var(--good)]" : "text-[var(--text-dim)]"}`} />
    </div>
    <div className="font-display font-black text-3xl">{value}</div>
  </div>
);
